import _ from 'lodash';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Redirect, Route, useHistory, useRouteMatch, Switch } from 'react-router-dom';
import { useMutation, useQuery } from '@apollo/client';

import { Button, Col, Nav, Row } from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';

import {
  CreateEmojiPackMutation,
  CreateEmojiPackMutationVariables,
  EmojiPack,
  EmojiPackLayout,
  EmojiPacksLayoutAvailabilitiesQuery,
  EmojiPacksLayoutAvailabilitiesQueryVariables,
  GetEmojiPacksQuery, 
  GetEmojiPacksQueryVariables,
} from '../../../../../../__gqltypes__';
import { CREATE_EMOJI_PACK, EMOJI_PACK_LAYOUT_INFO, GET_EMOJI_PACKS } from '../../../components/apolloQueries';
import { indexItems } from '../components/ItemsView';
import { LoadingLogo } from '../../../../devtools/components/Modal';
import EmojiPackView from '../components/EmojiPackView';
import CreateEmojiPackModal from '../components/CreateEmojiPackModal';
import { UploadFontModal } from '../components/UploadFontModal';
import EmojiFontDataModal from '../components/EmojiFontDataModal';

type EPLayoutAvailabilities = EmojiPacksLayoutAvailabilitiesQuery['liveOps']['emojiPacksLayoutAvailabilities'][0];

const EmojiPacksView =  <T extends string>({type}: {type: T}): JSX.Element => {
  const match = useRouteMatch();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadFontModal, setShowUploadModal] = useState(false);
  const [showFontInspectModal, setShowFontInspectModal] = useState(false);
  const [items, setItems] = useState<Record<string, EmojiPack> | null>(null);
  const { data, loading, refetch } = useQuery<
    GetEmojiPacksQuery,
    GetEmojiPacksQueryVariables
  >(GET_EMOJI_PACKS, { fetchPolicy: 'no-cache' });

  const {data: epLayoutData} = useQuery<
    EmojiPacksLayoutAvailabilitiesQuery,
    EmojiPacksLayoutAvailabilitiesQueryVariables
  >(EMOJI_PACK_LAYOUT_INFO);

  const epLAvai = useMemo(() => {
    const res = new Map<EmojiPackLayout, EPLayoutAvailabilities>();
    if (!epLayoutData) {
      return res;
    }
    epLayoutData.liveOps.emojiPacksLayoutAvailabilities.forEach((ep) => {
      res.set(ep.type, ep);
    });
    return res;
  }, [epLayoutData]);
  const history = useHistory();

  useEffect(() => {
    if (data) {
      setItems(indexItems(data?.liveOps?.emojiPacks?.list));
    }
  }, [data]);

  const amountOfEP = data?.liveOps?.emojiPacks?.list?.length ?? 0;

  const [createItem, { loading: createLoading }] = useMutation<
    CreateEmojiPackMutation, CreateEmojiPackMutationVariables
    >(CREATE_EMOJI_PACK);
  
  const handleCreate = useCallback((name: string) => {
    createItem({
      variables: {
        input: {
          name,
        },
      },
    })
      .then(({ data: newData }) => {
        setShowCreateModal(false);
        refetch().then(() => history.replace(`/sp3/liveops/items/emojis/${newData?.createEmojiPack.emojiPack.id}`));
      })
      .catch(() => {
        alert('Something went wrong :(');
      });
  }, [createItem, history, refetch]);

  const handleDelete = useCallback((id: string) => {
    const values = items ? Object.values(items) : [];
      if (values.length > 0) {
        const sortedPacks = _.sortBy(values, ['name']);
        let firstMatch = sortedPacks[0];
        if (firstMatch.id === id ) {
          if (sortedPacks.length > 2) {
            firstMatch = sortedPacks[1];
          } else {
            return;
          }
        }
        history.push(`/sp3/liveops/items/emojis/${firstMatch.id}`);
      }
  }, [history, items]);

  const styles: {[key: string]: React.CSSProperties } = useMemo(() => {
    return {
      navContainer: { 
        maxHeight: '75vh',
        overflowY: 'auto',
       },
    };
  }, []);
  const sortedItems = _.sortBy(items, ['name']);

  return (
    <>
      <LoadingLogo show={loading || createLoading} />
      <CreateEmojiPackModal 
        show={(showCreateModal) && !loading}
        loading={loading || createLoading}
        onHide={() => setShowCreateModal(false)}
        onAdd={handleCreate}
      />
      <EmojiFontDataModal
        show={showFontInspectModal}
        onHide={() => setShowFontInspectModal(false)}/>
      {showUploadFontModal && (
        <UploadFontModal onCancel={() => setShowUploadModal(false)}/>
      )}
      {items && (
        <Row className="d-flex">
          <Col>
            <h1 className="text-center">{type}</h1>
            <Button className="w-100 mb-3" variant="success" onClick={() => setShowCreateModal(true)}>
              Add a new {type}
            </Button>
            <Button className="w-100 mb-3" variant="primary" onClick={() => setShowUploadModal(true)}>
              Upload font
            </Button>
            <Button className="w-100 mb-3" variant="primary" onClick={() => setShowFontInspectModal(true)}>
              Show font data
            </Button>
            <Nav variant="pills" className="mb-3" style={styles.navContainer}>
              {Object.values(sortedItems).map((item) => (
                <Nav.Item key={item.id} className="text-center w-100">
                  <LinkContainer to={`${match.path}/${item.id}`}>
                    <Nav.Link>{item.name}</Nav.Link>
                  </LinkContainer>
                </Nav.Item>
              ))}
            </Nav>
          </Col>
          <Col className="col-10">
            <Switch>
              <Route
                path={`${match.path}/:assetId`}
                render={({ match: _match }) => {
                  return !!amountOfEP && <EmojiPackView
                    item={items[_match.params.assetId]}
                    refetch={refetch}
                    onDelete={handleDelete}
                    epLayoutAvai={epLAvai.get(items[_match.params.assetId].layout)}/>;
                }}
              />
              <Redirect to={`${match.path}/${Object.keys(items)[0]}`} />
            </Switch>
          </Col>
        </Row>
      )}
    </>
  );
};

export default EmojiPacksView;